import { z } from 'zod'
import { ROLES } from 'auth/roles'

// Shared by every pharmacist, not scoped to one provider. Carries an event for every managed-pharmacy
// order, not only re-triggers, and a failure event for every failed attempt (see `isFinalFailure`).
// Delivery is at-most-once, and some state changes never publish an event: prior-auth holds set by
// Scriptly webhooks, lock deferrals and skipped orders. An event is only a nudge to refetch; the API
// stays the source of truth, and a missing event never means "still processing".
export const ORDER_FULFILLMENT_TOPIC = 'web/providers/orderfulfillment'

export const OrderFulfillmentMqttEvent = {
  FULFILLED: 'orderfulfilled',
  FAILED: 'orderfulfillmentfailed',
} as const

// App.tsx's MQTT handler sits outside ReactQueryProvider, so it re-dispatches these events on the
// document for OrderFulfillmentEventsListener to pick up.
export const ORDER_FULFILLMENT_DOM_EVENT = 'qmd:orderfulfillment'

export const OrderFulfillmentEventSchema = z.object({
  EventType: z.enum([OrderFulfillmentMqttEvent.FULFILLED, OrderFulfillmentMqttEvent.FAILED]),
  OrderId: z.string().min(1),
  PatientId: z.string().min(1),
  Status: z.string().nullish(),
  PharmacyPartner: z.string().nullish(),
  Message: z.string().nullish(),
  // Failure events only. `IsFinal: false` means a retry is scheduled; absent or null is treated as
  // final, which is what a backend that sends one event per failure means.
  IsFinal: z.boolean().nullish(),
  AttemptNumber: z.number().int().nullish(),
  NextAttemptAtUtc: z.string().nullish(),
})

export type OrderFulfillmentEvent = z.infer<typeof OrderFulfillmentEventSchema>

/** A failure event with no retry scheduled; anything but an explicit `IsFinal: false` counts as final. */
export function isFinalFailure(event: Pick<OrderFulfillmentEvent, 'IsFinal'>): boolean {
  return event.IsFinal !== false
}

export function getOrderFulfillmentTopics(roles: readonly string[]): string[] {
  return roles.includes(ROLES.PHARMACIST) ? [ORDER_FULFILLMENT_TOPIC] : []
}
